/**
 * Script Tools
 *
 * AI agent tools for creating, editing, and inspecting custom scripts.
 */

import type { ToolDefinition } from './types';
import type { ToolContext } from '../agent-helpers';
import { resolveObject } from '../agent-helpers';
import { ScriptBehavior, dryRunHooks } from 'trace-viewer/behaviors';
import { gatherSceneContext, getScriptSource } from '../agent-context';
import { sharedSceneTool } from './shared-scene-tools';
import { getUndoManager, AddBehaviorCommand } from 'trace-viewer/editor';
import { dispatchBehaviorChanged } from '../../editor-events';

// ── create_script ───────────────────────────────────────────

async function executeCreateScript(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const name = input.name as string;
    const source = input.source as string;
    const objectRef = input.object as string | undefined;
    const { traceScene } = ctx;

    // Resolve target: explicit object param, or fall back to selected
    const targetObject = objectRef ? resolveObject(objectRef, ctx) : ctx.selectedObject;

    // Validate name
    if (!name?.trim()) {
        return 'Error: Script name is required.';
    }

    // Check for duplicate
    const scriptsMap = traceScene.scriptsMap;
    if (scriptsMap?.[name]) {
        return `Error: Script "${name}" already exists. Use edit_script to modify it.`;
    }

    // Register the script (compiles + stores blob)
    const assetPath = `./assets/scripts/${name}.ts`;
    traceScene.registerScript(name, assetPath, source);

    // Check for compilation errors
    const error = traceScene.scriptRegistry.getError(name);
    if (error) {
        return `Compilation error: ${error}\n\nPlease fix the source and try again.`;
    }

    // Verify factory works and dry-run all hooks to catch runtime errors
    const factory = traceScene.scriptRegistry.getFactory(name);
    if (!factory) {
        return `Script "${name}" compiled but factory not found.`;
    }
    const hookErrors = dryRunHooks(factory);
    if (hookErrors.length > 0) {
        return `Runtime error in script hooks:\n${hookErrors.join('\n')}\n\nPlease fix the source and try again.`;
    }

    // If there's a target object, attach the behavior
    if (targetObject) {
        const sb = await targetObject.addBehavior(name);

        // Record undo
        if (!getUndoManager().isUndoRedoing) {
            getUndoManager().push(new AddBehaviorCommand(
                targetObject,
                name,
                sb.getData(),
            ));
        }

        // Notify UI
        dispatchBehaviorChanged({
            objectPath: targetObject.getPath(),
            behaviorType: name,
            action: 'added',
        });

        return `Script "${name}" created and attached to "${targetObject.name}".`;
    }

    return `Script "${name}" created and registered. No target object to attach it to.`;
}

// ── edit_script ─────────────────────────────────────────────

function executeEditScript(
    input: Record<string, unknown>,
    ctx: ToolContext,
): string {
    const name = input.name as string;
    const source = input.source as string;
    const { traceScene } = ctx;

    // Verify script exists
    const scriptsMap = traceScene.scriptsMap;
    if (!scriptsMap?.[name]) {
        return `Error: Script "${name}" not found. Available scripts: ${Object.keys(scriptsMap ?? {}).join(', ') || '(none)'}`;
    }

    // Update blob in ResourceManager
    const assetPath = scriptsMap[name];
    const normalizedPath = assetPath.replace(/^\.\//, '');
    traceScene.resources.addAsset(
        normalizedPath,
        new Blob([source], { type: 'text/typescript' }),
    );

    // Re-register in ScriptRegistry (recompiles)
    traceScene.scriptRegistry.register(name, source);

    // Check for compilation success via factory existence
    const factory = traceScene.scriptRegistry.getFactory(name);
    if (!factory) {
        const error = traceScene.scriptRegistry.getError(name);
        return `Compilation error: ${error ?? 'Unknown error'}\n\nPlease fix the source and try again.`;
    }

    // Dry-run all hooks to catch runtime errors before hot-reloading
    const hookErrors = dryRunHooks(factory);
    if (hookErrors.length > 0) {
        return `Runtime error in script hooks:\n${hookErrors.join('\n')}\n\nPlease fix the source and try again.`;
    }

    // Hot-reload all instances of this script
    for (const sceneObj of traceScene.getSceneObjects()) {
        const behavior = sceneObj.getBehavior(name);
        if (behavior instanceof ScriptBehavior) {
            behavior.reloadFactory(factory);
        }
    }

    // Notify UI
    dispatchBehaviorChanged({
        objectPath: '',
        behaviorType: name,
        action: 'updated',
    });

    return `Script "${name}" updated and hot-reloaded.`;
}

// ── get_scene_structure ─────────────────────────────────────

function executeGetSceneStructure(
    _input: Record<string, unknown>,
    ctx: ToolContext,
): string {
    return gatherSceneContext(ctx.traceScene);
}

// ── get_script_source ───────────────────────────────────────

async function executeGetScriptSource(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const name = input.name as string;
    const source = await getScriptSource(ctx.traceScene, name);
    if (source === null) {
        const available = Object.keys(ctx.traceScene.scriptsMap ?? {});
        return `Script "${name}" not found. Available scripts: ${available.join(', ') || '(none)'}`;
    }
    return source;
}

// ── Exports ────────────────────────────────────────────────

export const scriptTools: ToolDefinition[] = [
    {
        tool: {
            name: 'create_script',
            input_schema: {
                type: 'object' as const,
                properties: {
                    name: { type: 'string' },
                    source: { type: 'string' },
                    object: { type: 'string' },
                },
                required: ['name', 'source'],
            },
        },
        execute: executeCreateScript,
        specialist: {},
    },
    {
        tool: {
            name: 'edit_script',
            input_schema: {
                type: 'object' as const,
                properties: {
                    name: { type: 'string' },
                    source: { type: 'string' },
                },
                required: ['name', 'source'],
            },
        },
        execute: (input, ctx) => Promise.resolve(executeEditScript(input, ctx)),
        specialist: {},
    },
    {
        tool: {
            name: 'get_scene_structure',
            input_schema: {
                type: 'object' as const,
                properties: {},
            },
        },
        execute: (input, ctx) => Promise.resolve(executeGetSceneStructure(input, ctx)),
    },
    sharedSceneTool('get_scene_tree'),
    {
        tool: {
            name: 'get_script_source',
            input_schema: {
                type: 'object' as const,
                properties: {
                    name: { type: 'string' },
                },
                required: ['name'],
            },
        },
        execute: executeGetScriptSource,
    },
];
